import { Link, useParams } from 'react-router-dom';
import Seo from '@/components/feature/Seo';
import Breadcrumb from '@/components/base/Breadcrumb';
import ShareButton from '@/components/feature/ShareButton';
import { CategoryTag } from '@/components/feature/NewsCard';
import WorkCard from '@/components/feature/WorkCard';
import { getNewsById, getRelatedNews, getWorkById } from '@/utils/content';
import NotFound from '@/pages/NotFound';

export default function NewsDetail() {
  const { id } = useParams();
  const item = getNewsById(id);

  if (!item) {
    return <NotFound />;
  }

  const relatedWork = item.relatedWorkId ? getWorkById(item.relatedWorkId) : undefined;
  const relatedNews = getRelatedNews(item, 3);

  return (
    <>
      <Seo
        title={`${item.title}｜深田咏美动态`}
        description={item.summary}
        keywords={`深田咏美新闻,${item.category},深田咏美动态`}
        ogImage={item.thumb}
      />

      <article className="bg-background-50">
        <div className="max-w-4xl mx-auto px-4 py-10 md:px-8 md:py-14">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: '新闻中心', path: '/news' },
              { label: '正文' },
            ]}
          />

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <CategoryTag category={item.category} />
            <span className="flex items-center gap-1.5 text-sm text-foreground-400">
              <i className="ri-calendar-line"></i>
              {item.date}
            </span>
          </div>

          <h1 className="mt-4 font-heading text-2xl font-black leading-snug text-foreground-950 md:text-4xl">
            {item.title}
          </h1>

          <div className="mt-7 aspect-[16/9] w-full overflow-hidden rounded-lg border border-background-200 bg-background-200">
            <img
              src={item.thumb}
              alt={`${item.title} 配图`}
              title={`${item.title} 深田咏美资讯`}
              className="h-full w-full object-top"
            />
          </div>

          <div className="mt-8 space-y-5">
            {item.content.map((paragraph, idx) => (
              <p key={idx} className="text-base leading-loose text-foreground-700">
                {paragraph}
              </p>
            ))}
          </div>

          <div className="mt-10 flex flex-wrap items-center gap-3 border-t border-background-200 pt-6">
            <Link
              to="/news"
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-foreground-950 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-foreground-800 cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              返回新闻列表
            </Link>
            <ShareButton />
          </div>
        </div>
      </article>

      {relatedWork && (
        <section className="border-t border-background-200 bg-background-100">
          <div className="max-w-6xl mx-auto px-4 py-14 md:px-8">
            <h2 className="mb-6 font-heading text-xl font-bold text-foreground-950 md:text-2xl">相关作品</h2>
            <div className="grid grid-cols-2 gap-6 sm:grid-cols-3 lg:grid-cols-4">
              <WorkCard item={relatedWork} />
            </div>
          </div>
        </section>
      )}

      <section className="bg-background-50">
        <div className="max-w-6xl mx-auto px-4 py-14 md:px-8 md:py-16">
          <h2 className="mb-6 font-heading text-xl font-bold text-foreground-950 md:text-2xl">相关资讯推荐</h2>
          <div className="flex flex-col divide-y divide-background-200 border-y border-background-200">
            {relatedNews.map((n) => (
              <Link
                key={n.id}
                to={`/news/${n.id}`}
                className="group flex items-center gap-4 py-5 transition-colors hover:bg-background-100/60 cursor-pointer"
              >
                <div className="hidden h-16 w-24 shrink-0 overflow-hidden rounded-md bg-background-200 sm:block">
                  <img
                    src={n.thumb}
                    alt={`${n.title} 缩略图`}
                    title={`${n.title} 深田咏美动态`}
                    className="h-full w-full object-top transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="line-clamp-2 font-heading text-sm font-bold leading-snug text-foreground-950 group-hover:text-primary-700 md:text-base">
                    {n.title}
                  </h3>
                  <div className="mt-2 flex items-center gap-3 text-xs text-foreground-400">
                    <CategoryTag category={n.category} />
                    <span>{n.date}</span>
                  </div>
                </div>
                <i className="ri-arrow-right-line shrink-0 text-foreground-400 transition-transform group-hover:translate-x-1 group-hover:text-primary-600"></i>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}